/* eslint-disable camelcase */
import toast from "react-hot-toast";
import { LoginResponse, ForgotPasswordResponse } from "../types";
import { getCookieToken } from "../utils";
import axios from "axios";

// Base URL for API
// const BASE_URL = "http://localhost:3001"; // Replace with your actual base URL
const { REACT_APP_BASE_URL } = process.env; // Fallback to localhost

// Create an Axios instance
export const apiClient = axios.create({
  baseURL: REACT_APP_BASE_URL,
  timeout: 20000, // Request timeout
  headers: {
    "Content-Type": "application/json",
  },
});

// Add token to requests if available (for authenticated endpoints)
apiClient.interceptors.request.use(
  (config) => {
    const token = getCookieToken("token");
    if (token) {
      config.headers = config.headers ?? {}; // Set to an empty object if undefined
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Export reusable API endpoints
export const apiService = {
  login: (data: { username: string; password: string }) => apiClient.post<LoginResponse>("/auth/login", data),
  forgotPassword: (email: string) =>
    apiClient.post<ForgotPasswordResponse>("/auth/request-password-reset", {
      email,
    }),
  changePassword: (token: string, newPassword: string) =>
    apiClient.post<ForgotPasswordResponse>("/auth/reset-password", {
      token,
      newPassword,
    }),

  // New method to create a folder
  createFolder: (folderName: string, folderType: string) =>
    // eslint-disable-next-line camelcase
    apiClient.post("/folders", {
      folder_name: folderName,
      folder_type: folderType,
    }),
  postFeedback: (feedbackData: {
    msg_id: number;
    user_id: number;
    remarks: string;
    comments: string;
    payload: {
      rating: number;
      tags: string[];
    };
  }) => {
    // Convert payload to JSON string
    const feedbackDataWithJsonPayload = {
      ...feedbackData,
      payload: JSON.stringify(feedbackData.payload),
    };
    console.log(feedbackDataWithJsonPayload);
    return apiClient.post("/feedback", feedbackDataWithJsonPayload);
  },

  fetchSidebar: () => apiClient.get("/chats"), // Make GET API call

  // Method to delete a chat
  deleteChat: (id: number) => apiClient.delete(`/chats/${id}`),

  // Method to delete a folder
  deleteFolder: (id: number) => apiClient.delete(`/folders/${id}`),

  renameFolder: (id: number, name: string) =>
    apiClient.patch(`/folders/${id}`, {
      folder_id: id, // The id of the folder
      folder_name: name, // The new name for the folder
    }),

  renameChat: (id: number, name: string) =>
    apiClient.put(`/chats/${id}`, {
      title: name, // The new name for the folder
    }),

  pinChat: (id: number) => apiClient.patch(`/chats/${id}/pin`),

  pinFolder: (id: number) => apiClient.patch(`/folders/${id}/pin`),

  fetchFolder: (folderName: string) => apiClient.post("/folders", { name: folderName }),

  getUserProfile: () => apiClient.get("/profile"),

  logout: () => apiClient.post("/logout"),

  fetchData: (endpoint: string, params?: object) => apiClient.get(endpoint, { params }),

  postData: (endpoint: string, data: object) => apiClient.post(endpoint, data),

  putData: (endpoint: string, data: object) => apiClient.put(endpoint, data),

  deleteData: (endpoint: string) => apiClient.delete(endpoint),
  // Method to create a chat with the specific payload
  createChat: (chatData: { user_id: number; org_id: number; title: string }) => apiClient.post("/chats", chatData), // Sends POST request to /chats
  updateChatDestimation: (chatData: { chat_id: number; folder_id: number }) =>
    apiClient.post(`/chats/${chatData.chat_id}/folder/${chatData.folder_id}`, {}), // Sends POST request to /chats
  getChatContext: (chatData: { query: string; chat_id: string; modelName: string }) => apiClient.post("/context", chatData), // Sends POST request to /chats
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getChatMessages: async (chatId: number): Promise<any> => {
    try {
      const response = await apiClient.get(`/chats/${chatId}`);
      return response.data; // Return only the 'data' object
    } catch (error) {
      toast.error("Error fetching chat messages:", {
        className: "md:!bg-[#FD7272]/20 !bg-[#FD7272]/100 dark:!text-white dark:text-[#191919] !shadow-none",
        duration: 2000,
      });
      return {
        chat: {
          id: 0,
          title: "",
          pinned: false,
          created_at: "",
          updated_at: "",
        },
        messages: [],
      };
    }
  },
  sendMessage: (messageData: {
    id?: number;
    chat_id: number;
    user_id: number;
    role: string;
    content: { role: string; content: React.ReactNode }[] | string;
    sequence: number;
  }) => {
    return apiClient.post("/messages", messageData);
  },
  sendMessageAgain: (messageData: {
    chat_id: number;
    user_id: number;
    role: string;
    id?: number;
    // content: string;
    content: { role: string; content: React.ReactNode }[];
    sequence: number;
  }) => apiClient.post("/messages", messageData),

  postShareChat: (chatId: number) => apiClient.post("/shared-chats", { chat_id: chatId }),
  shareChat: (chatId: number) => apiClient.get(`/shared-chats/${chatId}`), // Make GET API call
  doubleThumbsUp: (chatId: number, msgId: number) => apiClient.post("/feedback/double-thumbsup", { chat_id: chatId, msg_id: msgId }),
};
